/* De locatiemonitor op de projectpagina: wat de installatie nu doet en hoe de dag
 * verliep, uit ems.* met de sessie van de gebruiker (RLS beslist, ADR 0010).
 *
 * Bewust klein: de live stand (v_laatste_stand), de kwartieren van vandaag voor
 * een uurgrafiek, en de dagtotalen die daaruit volgen. Het volledige dashboard
 * met 3D en historie staat op de Site-pagina; daar linkt het blok naartoe.
 *
 * "Geen meting is geen licht" (ADR 0014): een waarde zonder meting is null, geen
 * nul, en een stand ouder dan een halfuur krijgt zijn eigen tijdstip mee. */

import { beginVanDag, lokaal, vandaagSleutel } from '@/features/ems/dashboard/data/tijd';

/** Alleen wat dit blok gebruikt van de Supabase-client; zo is het na te bootsen. */
export interface MonitorClient {
  from(tabel: string): any;
  schema(naam: string): { from(tabel: string): any };
}

export interface MonitorWaarde {
  kw: number | null;
  /** ISO-tijdstip van de meting, of null zonder meting. */
  tijdstip: string | null;
  /** Ouder dan VERSE_MINUTEN: de interface zet het tijdstip erbij. */
  oud: boolean;
}

export interface Monitor {
  locatieId: string;
  naam: string;
  plaats: string | null;
  siteId: string | null;
  tijdzone: string;
  netContractKw: number | null;
  sturingsdrempelKw: number | null;
  nu: {
    verbruik: MonitorWaarde;
    /** Positief = afname uit het net, negatief = teruglevering. */
    net: MonitorWaarde;
    zon: MonitorWaarde;
    /** Lading van alle batterijkasten samen. */
    batterij: { procent: number | null; kwh: number | null; capaciteitKwh: number | null; tijdstip: string | null; oud: boolean };
    laatste: string | null;
  };
  /** Gemiddeld vermogen per uur van vandaag (0..23); null = geen meting in dat uur. */
  uren: { verbruik: (number | null)[]; zon: (number | null)[]; net: (number | null)[] };
  vandaag: { verbruikKwh: number | null; zonKwh: number | null; netAfnameKwh: number | null; terugleveringKwh: number | null; uitBatterijKwh: number | null };
  installatie: { soort: 'zon' | 'batterij' | 'lader' | 'meter'; label: string }[];
}

export const VERSE_MINUTEN = 30;

const HOOFDMETER_VELDEN = ['verbruik', 'zon_locatie', 'net_import', 'net_export'] as const;
const BATTERIJ_VELDEN = ['bat_ontladen'] as const;

const getal = (v: unknown): number | null => (v === null || v === undefined || Number.isNaN(Number(v)) ? null : Number(v));

function waarde(stand: any[], assetIds: Set<string>, veld: string, nu: Date): MonitorWaarde {
  const rijen = stand.filter((r) => assetIds.has(String(r.asset_id)) && r.veld === veld && r.waarde !== null);
  if (rijen.length === 0) return { kw: null, tijdstip: null, oud: false };
  const tijdstip = rijen.map((r) => String(r.tijdstip)).sort().at(-1)!;
  const kw = rijen.reduce((s, r) => s + Number(r.waarde), 0);
  return { kw, tijdstip, oud: nu.getTime() - Date.parse(tijdstip) > VERSE_MINUTEN * 60_000 };
}

/** Kwartieren (kW) → gemiddelde per uur; een uur zonder meting blijft null. */
export function perUur(rijen: { tijdstip: string; waarde: number | null }[], zone: string, dag: string): (number | null)[] {
  const som = new Array<number>(24).fill(0);
  const n = new Array<number>(24).fill(0);
  for (const r of rijen) {
    if (r.waarde === null) continue;
    const l = lokaal(r.tijdstip, zone);
    if (l.dag !== dag) continue;
    const uur = Math.floor(l.i / 4);
    som[uur] += Number(r.waarde);
    n[uur]++;
  }
  return som.map((s, u) => (n[u] ? s / n[u] : null));
}

/** Kwartieren (kW) → kWh van de dag; null zonder één meting. */
export function kwhVanDag(rijen: { waarde: number | null }[]): number | null {
  const met = rijen.filter((r) => r.waarde !== null);
  return met.length ? met.reduce((s, r) => s + Number(r.waarde), 0) / 4 : null;
}

function installatie(assets: any[]): Monitor['installatie'] {
  const uit: Monitor['installatie'] = [];
  const zon = assets.filter((a) => a.type === 'omvormer');
  if (zon.length) {
    const kw = zon.reduce((s, a) => s + (getal(a.kwp) ?? getal(a.nominaal_kw) ?? 0), 0);
    uit.push({ soort: 'zon', label: `Zon ${Math.round(kw)} kW · ${zon.length} omvormer${zon.length === 1 ? '' : 's'}` });
  }
  const bat = assets.filter((a) => a.type === 'batterij');
  if (bat.length) {
    const kwh = bat.reduce((s, a) => s + (getal(a.capaciteit_kwh) ?? 0), 0);
    uit.push({ soort: 'batterij', label: `Batterij ${Math.round(kwh)} kWh · ${bat.length} kast${bat.length === 1 ? '' : 'en'}` });
  }
  const lader = assets.filter((a) => a.type === 'lader');
  const guns = assets.filter((a) => a.type === 'gun');
  for (const l of lader) {
    uit.push({ soort: 'lader', label: `${l.naam} ${Math.round(getal(l.nominaal_kw) ?? 0)} kW${guns.length ? ` · ${guns.length} guns` : ''}` });
  }
  if (assets.some((a) => a.type === 'netmeter')) uit.push({ soort: 'meter', label: 'Hoofdmeter' });
  return uit;
}

/**
 * De monitor van een project: via ems.locatie.project_id, of via de Site van het
 * project (ems.locatie.site_id, sites.project_id). Null als er geen actieve
 * locatie is — dan toont de pagina geen monitorblok.
 */
export async function haalMonitor(client: MonitorClient, projectId: string, nu: Date = new Date()): Promise<Monitor | null> {
  const { data: sites, error: siteFout } = await client.from('sites').select('id').eq('project_id', projectId);
  if (siteFout) throw siteFout;
  const siteIds = (sites ?? []).map((s: any) => String(s.id));

  let q = client.schema('ems').from('locatie')
    .select('id, naam, plaats, tijdzone, net_contract_kw, net_sturingsdrempel_kw, site_id, project_id')
    .eq('actief', true);
  q = siteIds.length ? q.or(`project_id.eq.${projectId},site_id.in.(${siteIds.join(',')})`) : q.eq('project_id', projectId);
  const { data: locaties, error: locFout } = await q.order('naam', { ascending: true });
  if (locFout) throw locFout;
  const loc = (locaties ?? [])[0];
  if (!loc) return null;

  const zone = String(loc.tijdzone || 'Europe/Amsterdam');
  const dag = vandaagSleutel(zone, nu);

  const { data: assets, error: assetFout } = await client.schema('ems').from('asset')
    .select('id, naam, type, nominaal_kw, capaciteit_kwh, kwp')
    .eq('locatie_id', loc.id).eq('actief', true);
  if (assetFout) throw assetFout;
  const alle = assets ?? [];
  const meter = new Set<string>(alle.filter((a: any) => a.type === 'netmeter').map((a: any) => String(a.id)));
  const batterijen = alle.filter((a: any) => a.type === 'batterij');
  const batIds = new Set<string>(batterijen.map((a: any) => String(a.id)));

  const { data: stand, error: standFout } = await client.schema('ems').from('v_laatste_stand')
    .select('asset_id, veld, waarde, tijdstip').eq('locatie_id', loc.id);
  if (standFout) throw standFout;
  const s = stand ?? [];

  const metingIds = [...meter, ...batIds];
  const velden = [...HOOFDMETER_VELDEN, ...BATTERIJ_VELDEN];
  const { data: metingen, error: metingFout } = metingIds.length
    ? await client.schema('ems').from('meting')
      .select('asset_id, tijdstip, veld, waarde')
      .in('asset_id', metingIds).in('veld', velden)
      .gte('tijdstip', beginVanDag(dag, zone).toISOString())
      .order('tijdstip', { ascending: true })
      .limit(5000)
    : { data: [], error: null };
  if (metingFout) throw metingFout;
  const m = (metingen ?? []).map((r: any) => ({ asset_id: String(r.asset_id), tijdstip: String(r.tijdstip), veld: String(r.veld), waarde: getal(r.waarde) }));
  const reeks = (ids: Set<string>, veld: string) => m.filter((r) => ids.has(r.asset_id) && r.veld === veld);

  const importNu = waarde(s, meter, 'net_import', nu);
  const exportNu = waarde(s, meter, 'net_export', nu);
  const net: MonitorWaarde = importNu.kw === null && exportNu.kw === null
    ? { kw: null, tijdstip: null, oud: false }
    : { kw: (importNu.kw ?? 0) - (exportNu.kw ?? 0), tijdstip: importNu.tijdstip ?? exportNu.tijdstip, oud: importNu.oud || exportNu.oud };

  const energie = waarde(s, batIds, 'bat_energie', nu);
  const capaciteit = batterijen.reduce((t: number, a: any) => t + (getal(a.capaciteit_kwh) ?? 0), 0);
  const verbruik = waarde(s, meter, 'verbruik', nu);
  const zon = waarde(s, meter, 'zon_locatie', nu);
  const tijden = [verbruik.tijdstip, net.tijdstip, zon.tijdstip, energie.tijdstip].filter(Boolean) as string[];

  const netUur = perUur(reeks(meter, 'net_import'), zone, dag).map((v, u) => {
    const terug = perUur(reeks(meter, 'net_export'), zone, dag)[u];
    return v === null && terug === null ? null : (v ?? 0) - (terug ?? 0);
  });

  return {
    locatieId: String(loc.id),
    naam: String(loc.naam),
    plaats: loc.plaats ?? null,
    siteId: loc.site_id ? String(loc.site_id) : null,
    tijdzone: zone,
    netContractKw: getal(loc.net_contract_kw),
    sturingsdrempelKw: getal(loc.net_sturingsdrempel_kw),
    nu: {
      verbruik,
      net,
      zon,
      batterij: {
        procent: energie.kw !== null && capaciteit > 0 ? (energie.kw / capaciteit) * 100 : null,
        kwh: energie.kw,
        capaciteitKwh: capaciteit > 0 ? capaciteit : null,
        tijdstip: energie.tijdstip,
        oud: energie.oud,
      },
      laatste: tijden.sort().at(-1) ?? null,
    },
    uren: {
      verbruik: perUur(reeks(meter, 'verbruik'), zone, dag),
      zon: perUur(reeks(meter, 'zon_locatie'), zone, dag),
      net: netUur,
    },
    vandaag: {
      verbruikKwh: kwhVanDag(reeks(meter, 'verbruik')),
      zonKwh: kwhVanDag(reeks(meter, 'zon_locatie')),
      netAfnameKwh: kwhVanDag(reeks(meter, 'net_import')),
      terugleveringKwh: kwhVanDag(reeks(meter, 'net_export')),
      uitBatterijKwh: kwhVanDag(reeks(batIds, 'bat_ontladen')),
    },
    installatie: installatie(alle),
  };
}
